import { throttle } from 'lodash';
import { useEffect, useMemo, useState, type RefObject } from 'react';
import { useEventListener } from 'usehooks-ts';

import { getCardAnchor, getCardAnchors } from '../../hooks';

import type { Point } from '../../types';

interface Props {
  svgRef: RefObject<SVGSVGElement>;
  ids: string[]; // which cards to draw
  pointsById: Record<string, Point | undefined>; // pre-projected SVG coords
  moving?: boolean;
}

const clientToSvg = (svg: SVGSVGElement, x: number, y: number) => {
  const ctm = svg.getScreenCTM();
  if (!ctm) {
    return null;
  }
  const { x: sx, y: sy } = new DOMPoint(x, y).matrixTransform(ctm.inverse());
  return { x: sx, y: sy };
};

// Check a point given in *SVG coords* against the SVG's viewport
const isSvgPointInViewport = (
  svg: SVGSVGElement,
  sx: number,
  sy: number,
  m = 10,
) => {
  const rect = svg.getBoundingClientRect();
  const ctm = svg.getScreenCTM();
  if (!ctm) {
    return false;
  }
  const { x, y } = new DOMPoint(sx, sy).matrixTransform(ctm); // to client
  return (
    x >= rect.left - m &&
    x <= rect.right + m &&
    y >= rect.top - m &&
    y <= rect.bottom + m
  );
};

// Check a point given in *client coords* against the SVG's viewport
const isClientPointInViewport = (
  svg: SVGSVGElement,
  cx: number,
  cy: number,
  m = 10,
) => {
  const rect = svg.getBoundingClientRect();
  return (
    cx >= rect.left - m &&
    cx <= rect.right + m &&
    cy >= rect.top - m &&
    cy <= rect.bottom + m
  );
};

export const CaseCardLinkLines = ({
  svgRef,
  ids,
  pointsById,
  moving = false,
}: Props) => {
  // we only need the setter to force re-renders
  const [, setTick] = useState(0);

  const bump = useMemo(
    () =>
      // 16ms throttle = ~60fps update rate (1000ms / 60fps ≈ 16.67ms)
      // Matches browser refresh rate for smooth line animations
      throttle(() => setTick(t => (t + 1) % 1_000_000), 16, {
        leading: true,
        trailing: true,
      }),
    [],
  );

  useEffect(
    () => () => {
      bump.cancel();
    },
    [bump],
  );

  // Recalc when window scrolls/resizes (cards move / layout shifts)
  useEventListener('scroll', bump, undefined, { passive: true });
  useEventListener('resize', bump, undefined, { passive: true });

  // Recalc when the SVG element itself resizes
  useEffect(() => {
    const el = svgRef.current;
    if (!el) {
      return;
    }

    const ro = new ResizeObserver(bump);
    ro.observe(el);
    return () => ro.disconnect();
  }, [svgRef, bump]);

  // Important: recompute once right after movement ends (true -> false)
  useEffect(() => {
    if (!moving) {
      bump();
    }
  }, [moving, bump]);

  const idsString = ids.join(',');

  // rAF watcher to follow carousel movement
  useEffect(() => {
    let anchors = getCardAnchors().map(e => e.el);

    if (!anchors.length) {
      return;
    }

    const snapRects = () =>
      anchors.map(a => {
        const r = a.getBoundingClientRect();
        return `${Math.round(r.left)}:${Math.round(r.top)}:${Math.round(r.width)}:${Math.round(r.height)}`;
      });

    let prev = snapRects();
    let rafId = 0;
    let idleFrames = 0;
    let rescanCounter = 0;

    const loop = () => {
      if (moving) {
        rafId = requestAnimationFrame(loop);
        return;
      }

      const curr = snapRects();
      let changed = curr.length !== prev.length;
      for (let i = 0; !changed && i < curr.length; i++) {
        if (curr[i] !== prev[i]) {
          changed = true;
        }
      }

      if (changed) {
        prev = curr;
        idleFrames = 0;
        rescanCounter = 0;
        bump();
      } else {
        idleFrames++;
        rescanCounter++;
        if (rescanCounter >= 10) {
          anchors = getCardAnchors().map(e => e.el);
          prev = snapRects();
          rescanCounter = 0;
        }
      }

      const delayFrames = idleFrames > 30 ? 6 : 1;
      if (delayFrames === 1) {
        rafId = requestAnimationFrame(loop);
      } else {
        let hops = 0;
        const hop = () => {
          hops++;
          rafId =
            hops >= delayFrames
              ? requestAnimationFrame(loop)
              : requestAnimationFrame(hop);
        };
        rafId = requestAnimationFrame(hop);
      }
    };

    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [idsString, bump, moving]);

  // recompute on each render (renders are already throttled by bump)
  const svg = svgRef.current;
  let links:
    | {
        id: string;
        card: { x: number; y: number };
        dot: { x: number; y: number };
      }[]
    | null = [];

  if (moving) {
    links = null;
  } else if (!svg) {
    links = [];
  } else {
    links = ids.reduce<typeof links>((acc, id) => {
      const entry = getCardAnchor(id);
      if (!entry) {
        return acc;
      }

      // CARD anchor (client space)
      const rect = entry.el.getBoundingClientRect();
      const edgeX = entry.side === 'left' ? rect.right : rect.left;
      const cx = edgeX;
      const cy = rect.top + rect.height / 2;

      // Viewport gate for CARD (client coords)
      if (!isClientPointInViewport(svg, cx, cy, 30)) {
        return acc;
      }

      // Project DOT into SVG space
      const dotPt = pointsById[id];
      if (!dotPt) {
        return acc;
      }

      // Viewport gate for DOT (svg coords)
      const [dx, dy] = dotPt;
      if (!isSvgPointInViewport(svg, dx, dy, 10)) {
        return acc;
      }

      const svgCard = clientToSvg(svg, cx, cy);
      if (!svgCard) {
        return acc;
      }

      acc.push({ id, card: svgCard, dot: { x: dx, y: dy } });
      return acc;
    }, []);
  }

  // No links to draw
  if (links === null || links.length === 0) {
    return null;
  }

  return (
    <g className="case-card-link-lines" pointerEvents="none">
      <defs>
        {links.map(({ id, card, dot }) => (
          <linearGradient
            key={`grad-${id}`}
            id={`card-line-grad-${id}`}
            gradientUnits="userSpaceOnUse"
            x1={card.x}
            y1={card.y}
            x2={dot.x}
            y2={dot.y}
            spreadMethod="pad"
          >
            {/* smooth head ~20% then faint tail */}
            <stop offset="0%" stopColor="rgba(255,174,129,0.7)" />
            <stop offset="5%" stopColor="rgba(255,174,129,0.6)" />
            <stop offset="12%" stopColor="rgba(255,174,129,0.45)" />
            <stop offset="20%" stopColor="rgba(255,174,129,0.25)" />
            <stop offset="90%" stopColor="rgba(255,174,129,0.1)" />
            <stop offset="97%" stopColor="rgba(255,174,129,0.05)" />
            <stop offset="100%" stopColor="rgba(255,174,129,0)" />
          </linearGradient>
        ))}
      </defs>

      {links.map(({ id, card, dot }) => (
        <path
          key={id}
          d={`M ${card.x},${card.y} L ${dot.x},${dot.y}`}
          stroke={`url(#card-line-grad-${id})`}
          strokeWidth={1.5}
          strokeLinecap="round"
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </g>
  );
};
